<template>
    <div class="border border-gray rounded p-3 mt-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="font-weight-bold mb-0">{{ $t('individualCommissions') }}</h6>
            <b-dropdown
                v-if="!showAddForm"
                size="sm"
                :text="$t('addIndividualCommission')"
                variant="secondary"
                right
                :disabled="requestCommissionSpaces.getLoading()"
            >
                <b-dropdown-item @click="openAddForm('percent')">{{ $t('addPercentCommission') }}</b-dropdown-item>
                <b-dropdown-item @click="openAddForm('amount')">{{ $t('addAmountCommission') }}</b-dropdown-item>
            </b-dropdown>
        </div>

        <ErrorMessage :value="requestCommissionSpaces.getError() || requestCommissionSpace.getError()" />

        <div v-if="requestCommissionSpaces.getLoading()" class="d-flex justify-content-center my-3">
            <b-spinner />
        </div>

        <div v-else>

            <b-table
                v-if="items.length > 0"
                :items="items"
                :fields="fields"
                small
                class="mb-2"
                bordered
                responsive
            >
                <template #cell(space)="{ item }">
                    <div>
                        <strong>{{ item.space.name }}</strong>
                        <div class="text-muted small">{{ item.space.url }}</div>
                    </div>
                </template>

                <template #cell(adminCommission)="{ item, index }">
                    <b-input-group size="sm">
                        <b-form-input
                            v-model="editForm[index].adminCommission"
                            v-decimal="{ min: 0, max: item.type === 'percent' ? 100 : undefined, spinButtons: 'horizontal' }"
                            type="text"
                            :state="$v.editForm.$each[index] && $v.editForm.$each[index].adminCommission.$dirty && $v.editForm.$each[index].adminCommission.$error ? false : null"
                            :title="$v.editForm.$each[index] && $v.editForm.$each[index].adminCommission.$error ? (!$v.editForm.$each[index].adminCommission.decimal ? $t('validator.decimal') : !$v.editForm.$each[index].adminCommission.maxHundred ? $t('validator.max.numeric', { max: 100 }) : $t('validator.min.numeric', { min: 0 })) : null"
                        />
                        <b-input-group-append>
                            <span class="input-group-text">{{ item.type === 'amount' ? $currencySymbol : '%' }}</span>
                        </b-input-group-append>
                    </b-input-group>
                </template>

                <template #cell(publisherCommission)="{ item, index }">
                    <b-input-group size="sm">
                        <b-form-input
                            v-model="editForm[index].publisherCommission"
                            v-decimal="{ min: 0, max: item.type === 'percent' ? 100 : undefined, spinButtons: 'horizontal' }"
                            type="text"
                            :state="$v.editForm.$each[index] && $v.editForm.$each[index].publisherCommission.$dirty && $v.editForm.$each[index].publisherCommission.$error ? false : null"
                            :title="$v.editForm.$each[index] && $v.editForm.$each[index].publisherCommission.$error ? (!$v.editForm.$each[index].publisherCommission.decimal ? $t('validator.decimal') : !$v.editForm.$each[index].publisherCommission.maxHundred ? $t('validator.max.numeric', { max: 100 }) : $t('validator.min.numeric', { min: 0 })) : null"
                        />
                        <b-input-group-append>
                            <span class="input-group-text">{{ item.type === 'amount' ? $currencySymbol : '%' }}</span>
                        </b-input-group-append>
                    </b-input-group>
                </template>

                <template #cell(actions)="{ item, index }">
                    <div class="d-flex gap-1">
                        <ButtonIcon
                            size="sm"
                            variant="success"
                            :loading="requestCommissionSpace.getLoading(`update-${index}`)"
                            :disabled="requestCommissionSpace.getLoading(`update-${index}`) || requestCommissionSpace.getLoading(`delete-${index}`)"
                            hide-label-while-loading
                            @click="updateItem(item, index)"
                        >
                            {{ $t('save') }}
                        </ButtonIcon>
                        <ButtonIcon
                            size="sm"
                            variant="danger"
                            :loading="requestCommissionSpace.getLoading(`delete-${index}`)"
                            :disabled="requestCommissionSpace.getLoading(`delete-${index}`) || requestCommissionSpace.getLoading(`update-${index}`)"
                            hide-label-while-loading
                            @click="deleteItem(item, index)"
                        >
                            {{ $t('remove') }}
                        </ButtonIcon>
                    </div>
                </template>
            </b-table>

            <template v-if="showAddForm">
                <b-form class="mt-2 mb-2 p-3 border border-gray rounded" novalidate @submit.prevent="createItem">
                    <div class="d-flex gap-4">
                        <div class="flex-grow-1 width-min-150 w-100">
                            <FormValidator class="mb-0 width-min-0 w-100" form-group :validator="$v.addForm.space" :label="$t('space')">
                                <MultiselectSpaces
                                    v-model="addForm.space"
                                    :multiple="false"
                                    :show-checkboxes="false"
                                    :close-on-select="true"
                                    :show-selected="false"
                                    :placeholder="$t('space')"
                                    class="width-min-0"
                                />
                            </FormValidator>
                        </div>
                        <div class="flex-grow-1 flex-basis-0">
                            <FormValidator
                                class="mb-0 width-min-150"
                                form-group
                                :validator="$v.addForm.publisherCommission"
                                :label="$t('publisherCommission')"
                                :msg-props="isPercent ? { min: 0, max: 100 } : { min: 0 }"
                                v-slot="slotProps"
                            >
                                <b-input-group>
                                    <b-form-input
                                        v-model="addForm.publisherCommission"
                                        v-decimal="{ min: 0, max: isPercent ? 100 : undefined, spinButtons: 'horizontal' }"
                                        type="text"
                                        placeholder="0"
                                        :state="slotProps.state"
                                    />
                                    <b-input-group-append>
                                        <span class="input-group-text">{{ commissionSuffix }}</span>
                                    </b-input-group-append>
                                </b-input-group>
                            </FormValidator>
                        </div>
                        <div class="flex-grow-1 flex-basis-0">
                            <FormValidator
                                class="mb-0 width-min-150"
                                form-group
                                :validator="$v.addForm.adminCommission"
                                :label="$t('adminCommission')"
                                :msg-props="isPercent ? { min: 0, max: 100 } : { min: 0 }"
                                v-slot="slotProps"
                            >
                                <b-input-group>
                                    <b-form-input
                                        v-model="addForm.adminCommission"
                                        v-decimal="{ min: 0, max: isPercent ? 100 : undefined, spinButtons: 'horizontal' }"
                                        type="text"
                                        placeholder="0"
                                        :state="slotProps.state"
                                    />
                                    <b-input-group-append>
                                        <span class="input-group-text">{{ commissionSuffix }}</span>
                                    </b-input-group-append>
                                </b-input-group>
                            </FormValidator>
                        </div>
                        <div class="flex-grow-1 flex-basis-0">
                            <b-form-group class="mb-0">
                                <template #label>&nbsp;</template>
                                <div class="d-flex gap-2">
                                    <ButtonIcon
                                        type="submit"
                                        variant="success"
                                        :loading="requestCommissionSpace.getLoading('post')"
                                        :disabled="requestCommissionSpace.getLoading('post')"
                                        hide-label-while-loading
                                    >
                                        {{ $t('save') }}
                                    </ButtonIcon>
                                    <ButtonIcon
                                        variant="secondary"
                                        :disabled="requestCommissionSpace.getLoading('post')"
                                        @click="cancelAdd"
                                    >
                                        {{ $t('cancel') }}
                                    </ButtonIcon>
                                </div>
                            </b-form-group>
                        </div>
                    </div>
                </b-form>
            </template>

        </div>
    </div>
</template>



<script>
    import { cloneDeep } from 'lodash';
    import { between, minValue, decimal } from 'vuelidate/lib/validators';
    import ErrorMessage from '@/components/common/ErrorMessage.vue';
    import FormValidator from '@/components/common/FormValidator.vue';
    import MultiselectSpaces from '@/components/common/MultiselectSpaces.vue';

    const FORM_DEFAULT_DATA = {
        space: null,
        adminCommission: 0,
        publisherCommission: 0,
    };

    export default {
        name: 'CommissionSpaces',
        components: {
            ErrorMessage,
            FormValidator,
            MultiselectSpaces,
        },
        validations() {
            const commissionRule = this.addFormType === 'percent' ? { decimal, between: between(0, 100) } : { decimal, minValue: minValue(0) };
            return {
                addForm: {
                    space: { required: val => !!val?.id },
                    adminCommission: commissionRule,
                    publisherCommission: commissionRule,
                },
                editForm: {
                    $each: {
                        adminCommission: { decimal, minValue: minValue(0), maxHundred: (val, row) => row.type !== 'percent' || parseFloat(val) <= 100 },
                        publisherCommission: { decimal, minValue: minValue(0), maxHundred: (val, row) => row.type !== 'percent' || parseFloat(val) <= 100 },
                    },
                },
            };
        },
        props: {
            commissionId: {
                type: Number,
                required: true,
            },
        },
        data() {
            return {
                requestCommissionSpaces: this.$req('campaigns/CommissionSpaces'),
                requestCommissionSpace: this.$req('campaigns/CommissionSpace'),
                items: [],
                editForm: [],
                addForm: cloneDeep(FORM_DEFAULT_DATA),
                addFormType: 'percent',
                showAddForm: false,
            };
        },
        computed: {
            isPercent() {
                return this.addFormType === 'percent';
            },
            commissionSuffix() {
                return this.isPercent ? '%' : this.$currencySymbol;
            },
            fields() {
                return [
                    { key: 'space', label: this.$t('space') },
                    { key: 'campaign', label: this.$t('campaign'), formatter: v => v?.name },
                    { key: 'adminCommission', label: this.$t('adminCommission') },
                    { key: 'publisherCommission', label: this.$t('publisherCommission') },
                    { key: 'actions', label: '' },
                ];
            },
        },
        methods: {
            loadItems() {
                this.requestCommissionSpaces.get({ urlParams: { commissionId: this.commissionId } }).then(data => {
                    this.items = data?.items || [];
                    this.editForm = this.items.map(item => ({
                        adminCommission: item.adminCommission,
                        publisherCommission: item.publisherCommission,
                        type: item.type,
                    }));
                });
            },
            createItem() {
                this.$v.addForm.$touch();
                if (this.$v.addForm.$invalid) return;

                const { space, adminCommission, publisherCommission } = this.addForm;
                const spaceId = space.id;

                this.requestCommissionSpace
                    .id('post')
                    .post({ urlParams: { commissionId: this.commissionId, spaceId }, requestParams: { adminCommission: parseFloat(adminCommission), publisherCommission: parseFloat(publisherCommission) } })
                    .then(data => {
                        this.items.push({ ...data, type: data.type || this.addFormType });
                        this.editForm.push({
                            adminCommission: data.adminCommission,
                            publisherCommission: data.publisherCommission,
                            type: data.type || this.addFormType,
                        });
                        this.addForm = cloneDeep(FORM_DEFAULT_DATA);
                        this.$v.addForm.$reset();
                        this.showAddForm = false;
                        this.$toastr.success(this.$t('dataSavedSuccessfully'));
                    })
                    .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
            },
            updateItem(item, index) {
                this.$v.editForm.$each[index].$touch();
                if (this.$v.editForm.$each[index].$invalid) return;

                const { adminCommission, publisherCommission } = this.editForm[index];

                this.requestCommissionSpace
                    .id(`update-${index}`)
                    .patch({ urlParams: { commissionId: this.commissionId, spaceId: item.space.id }, requestParams: { adminCommission: parseFloat(adminCommission), publisherCommission: parseFloat(publisherCommission) } })
                    .then(data => {
                        this.$set(this.items, index, { ...data, type: data.type || item.type });
                        this.$set(this.editForm, index, {
                            adminCommission: data.adminCommission,
                            publisherCommission: data.publisherCommission,
                            type: data.type || item.type,
                        });
                        this.$toastr.success(this.$t('dataSavedSuccessfully'));
                    })
                    .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
            },
            deleteItem(item, index) {
                this.requestCommissionSpace
                    .id(`delete-${index}`)
                    .delete({ urlParams: { commissionId: this.commissionId, spaceId: item.space.id } })
                    .then(() => {
                        this.$delete(this.items, index);
                        this.$delete(this.editForm, index);
                        this.$toastr.success(this.$t('dataSavedSuccessfully'));
                    })
                    .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
            },
            cancelAdd() {
                this.addForm = cloneDeep(FORM_DEFAULT_DATA);
                this.$v.addForm.$reset();
                this.showAddForm = false;
            },
            openAddForm(type) {
                this.addFormType = type;
                this.addForm = cloneDeep(FORM_DEFAULT_DATA);
                this.$v.addForm.$reset();
                this.showAddForm = true;
            },
        },
        created() {
            this.loadItems();
        },
    };
</script>
